import {
  useCallback,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
  useRef,
} from 'react';
import { preventLeadingWhitespace } from '@/utils/helperFunctions';
import clsx from 'clsx';
import { berthCategoryValidationSchema } from '@/utils/validations';
import { Modal } from '@/components';
import CircularProgress from '@mui/material/CircularProgress';
import {
  IBerthCategoryData,
  IBerthCategoryDataResponse,
} from '@/types/berth-category.types';
import { useGetCategoryByIdQuery } from '@/services/BerthCategoryService';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { useGetHarborsQuery } from '@/services/HarborManagementService';
import NumberInput from '@/components/inputControls/NumberInput';

interface BerthCategoryModalProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit' | 'view';
  categoryId?: string;
  formData: Partial<IBerthCategoryData>;
  setFormData: Dispatch<SetStateAction<Partial<IBerthCategoryData>>>;
  errors: { [key: string]: string };
  setErrors: Dispatch<SetStateAction<{ [key: string]: string }>>;
  onSubmit: (formData: Partial<IBerthCategoryData>) => Promise<void>;
  isSubmitting?: boolean;
  validateField?: (name: string, value: unknown) => Promise<void>;
  className?: string;
}

const BerthCategoryModal = ({
  mode,
  categoryId,
  formData: initialFormData,
  setFormData,
  errors,
  setErrors,
  onSubmit: onSubmitProp,
  isSubmitting,
  open,
  onClose,
  className,
  ...props
}: BerthCategoryModalProps) => {
  const [localFormData, setLocalFormData] =
    useState<Partial<IBerthCategoryData>>(initialFormData);

  const nameInputRef = useRef<HTMLInputElement>(null);
  const selectedHarborId = useSelector(
    (state: RootState) => state.auth.selectedHarborId
  );
  const { data: harborsData, isLoading: isHarborsLoading } = useGetHarborsQuery(
    {}
  );
  const [harborOptions, setHarborOptions] = useState<
    { id: string; name: string }[]
  >([]);

  // Update local form data when prop changes
  useEffect(() => {
    setLocalFormData(initialFormData);
  }, [initialFormData]);

  useEffect(() => {
    if (harborsData?.data) {
      const options = harborsData.data.map((harbor) => ({
        id: harbor.id,
        name: harbor.name,
      }));
      if (JSON.stringify(options) !== JSON.stringify(harborOptions)) {
        setHarborOptions(options);
      }
    }
  }, [harborsData, harborOptions]);

  const { data: categoryResponse, isLoading: isCategoryLoading } =
    useGetCategoryByIdQuery(categoryId || '', {
      skip: !categoryId || mode === 'create',
    }) as { data: IBerthCategoryDataResponse | undefined; isLoading: boolean };

  const categoryData = categoryResponse?.data;

  useEffect(() => {
    if (categoryData && (mode === 'view' || mode === 'edit')) {
      setLocalFormData(categoryData);
    }
  }, [categoryData, mode]);

  // Focus the Name field when the modal opens (except in view mode)
  useEffect(() => {
    if (open && mode !== 'view') {
      const timer = setTimeout(() => {
        nameInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, mode]);

  const validateField = useCallback(
    async (name: keyof IBerthCategoryData | string, value: unknown) => {
      try {
        await berthCategoryValidationSchema.validateAt(
          name,
          { ...localFormData, [name]: value },
          { abortEarly: true }
        );
        setErrors((prev) => ({
          ...prev,
          [name]: '',
        }));
      } catch (err) {
        const error = err as Error;
        setErrors((prev) => ({
          ...prev,
          [name]: error.message,
        }));
      }
    },
    [localFormData, setErrors]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setLocalFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      validateField(name, value);
    },
    [validateField]
  );

  const handleNumberInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
      const value =
        e.target.value === '' ? undefined : parseFloat(e.target.value);
      setLocalFormData((prev) => ({
        ...prev,
        [fieldName]: value,
      }));
      validateField(fieldName, value);
    },
    [validateField]
  );

  const handleSelectChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { name, value } = e.target;
      setLocalFormData((prev) => ({
        ...prev,
        [name]: parseInt(value, 10),
      }));
      validateField(name, parseInt(value, 10));
    },
    [validateField]
  );

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      // Update parent component with latest form data before submission
      setFormData(localFormData);
      await onSubmitProp(localFormData);
    },
    [localFormData, onSubmitProp, setFormData]
  );

  if (isCategoryLoading) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        className="!flex items-center justify-center"
      >
        <div className="!flex justify-center p-8">
          <CircularProgress aria-label="Loading berth category details" />
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={`!flex items-center justify-center ${className}`}
      aria-labelledby="berth-category-modal-title"
      {...props}
    >
      <div className="bg-gray-100 p-8 w-full max-w-2xl mx-4 rounded-xl shadow-2xl flex flex-col responsive-form berth-category-modal">
        <h1
          id="berth-category-modal-title"
          className="text-2xl font-semibold text-gray-800 mb-6"
        >
          {mode === 'create'
            ? 'Create Berth Category'
            : mode === 'edit'
              ? 'Edit Berth Category'
              : 'View Berth Category'}
        </h1>
        <div id="berth-category-modal-description" className="sr-only">
          {mode === 'create'
            ? 'Form to create a new berth category'
            : mode === 'edit'
              ? 'Form to edit berth category details'
              : 'View berth category details'}
        </div>
        <div className="flex-1 overflow-y-auto max-h-[70vh] pr-2 custom-scrollbar">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-6">
            {/* Harbor (disabled). Create: selectedHarborId. Edit: localFormData.harbor_id */}
            {(mode === 'create' || mode === 'edit') && (
              <div className="col-span-1">
                <label
                  htmlFor="harbor-select"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Harbor
                </label>
                <select
                  id="harbor-select"
                  name="harbor_id"
                  value={
                    mode === 'create'
                      ? selectedHarborId || ''
                      : (localFormData.harbor_id as string) ||
                        selectedHarborId ||
                        ''
                  }
                  disabled
                  className={clsx(
                    'select select-bordered w-full h-11 rounded-lg border-gray-300'
                  )}
                >
                  <option value="">
                    {isHarborsLoading ? 'Loading harbors...' : 'Select harbor'}
                  </option>
                  {harborOptions.map((h) => (
                    <option key={h.id} value={h.id}>
                      {h.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {/* Name */}
            <div className="col-span-1">
              <label
                htmlFor="name-input"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Name
              </label>
              <input
                id="name-input"
                type="text"
                name="name"
                value={localFormData.name || ''}
                ref={nameInputRef}
                onChange={handleInputChange}
                onKeyDown={preventLeadingWhitespace}
                onBlur={(e) => validateField(e.target.name, e.target.value)}
                disabled={mode === 'view' || isSubmitting}
                className={clsx(
                  'input input-bordered w-full h-11 rounded-lg border-gray-300',
                  errors.name && 'border-red-500'
                )}
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? 'name-error' : undefined}
                placeholder="Enter category name (e.g., Medium Vessel)"
              />
              {errors.name && (
                <p
                  id="name-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.name}
                </p>
              )}
            </div>

            {/* Status */}
            <div className="col-span-1">
              <label
                htmlFor="status"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Status
              </label>
              <select
                id="status"
                name="status"
                value={
                  localFormData.status !== undefined ? localFormData.status : ''
                }
                onChange={handleSelectChange}
                onBlur={(e) =>
                  validateField(e.target.name, parseInt(e.target.value, 10))
                }
                disabled={mode === 'view' || isSubmitting}
                className={clsx(
                  'select select-bordered w-full h-11 rounded-lg border-gray-300',
                  errors.status && 'border-red-500'
                )}
              >
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
              {errors.status && (
                <p
                  id="status-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.status}
                </p>
              )}
            </div>

            {/* Description */}
            <div className="col-span-2">
              <label
                htmlFor="description-input"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Description
              </label>
              <textarea
                id="description-input"
                name="description"
                value={localFormData.description || ''}
                onChange={handleInputChange}
                onKeyDown={preventLeadingWhitespace}
                onBlur={(e) => validateField(e.target.name, e.target.value)}
                disabled={mode === 'view' || isSubmitting}
                className={clsx(
                  'textarea textarea-bordered w-full rounded-lg border-gray-300',
                  errors.description && 'border-red-500'
                )}
                aria-invalid={!!errors.description}
                aria-describedby={
                  errors.description ? 'description-error' : undefined
                }
                placeholder="Enter category description"
                rows={4}
              />
              {errors.description && (
                <p
                  id="description-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.description}
                </p>
              )}
            </div>

            {/* Dimensions */}
            <div className="col-span-2">
              <h2 className="text-lg font-medium text-gray-800 mb-4">
                Dimensions
              </h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="col-span-1">
                  <label
                    htmlFor="min_length-input"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Minimum Length (m)
                  </label>
                  <NumberInput
                    id="min_length-input"
                    name="min_length"
                    value={localFormData.min_length ?? ''}
                    onChange={(e) => handleNumberInputChange(e, 'min_length')}
                    disabled={mode === 'view' || isSubmitting}
                    className={clsx(
                      'input input-bordered w-full h-11 rounded-lg border-gray-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
                      errors.min_length && 'border-red-500'
                    )}
                    aria-invalid={!!errors.min_length}
                    aria-describedby={
                      errors.min_length ? 'min_length-error' : undefined
                    }
                    placeholder="Enter minimum length (m)"
                    step="0.01"
                    min="1"
                    max="1000"
                    autoComplete="off"
                  />
                  {errors.min_length && (
                    <p
                      id="min_length-error"
                      className="text-red-500 text-xs mt-1"
                      role="alert"
                    >
                      {errors.min_length}
                    </p>
                  )}
                </div>

                <div className="col-span-1">
                  <label
                    htmlFor="max_length-input"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Maximum Length (m)
                  </label>
                  <NumberInput
                    id="max_length-input"
                    name="max_length"
                    value={localFormData.max_length ?? ''}
                    onChange={(e) => handleNumberInputChange(e, 'max_length')}
                    disabled={mode === 'view' || isSubmitting}
                    className={clsx(
                      'input input-bordered w-full h-11 rounded-lg border-gray-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
                      errors.max_length && 'border-red-500'
                    )}
                    aria-invalid={!!errors.max_length}
                    aria-describedby={
                      errors.max_length ? 'max_length-error' : undefined
                    }
                    placeholder="Enter maximum length (m)"
                    step="0.01"
                    min="1"
                    max="1000"
                    autoComplete="off"
                  />
                  {errors.max_length && (
                    <p
                      id="max_length-error"
                      className="text-red-500 text-xs mt-1"
                      role="alert"
                    >
                      {errors.max_length}
                    </p>
                  )}
                </div>

                <div className="col-span-1">
                  <label
                    htmlFor="min_width-input"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Minimum Width (m)
                  </label>
                  <NumberInput
                    id="min_width-input"
                    name="min_width"
                    value={localFormData.min_width ?? ''}
                    onChange={(e) => handleNumberInputChange(e, 'min_width')}
                    disabled={mode === 'view' || isSubmitting}
                    className={clsx(
                      'input input-bordered w-full h-11 rounded-lg border-gray-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
                      errors.min_width && 'border-red-500'
                    )}
                    aria-invalid={!!errors.min_width}
                    aria-describedby={
                      errors.min_width ? 'min_width-error' : undefined
                    }
                    placeholder="Enter minimum width (m)"
                    step="0.01"
                    min="1"
                    max="100"
                    autoComplete="off"
                  />
                  {errors.min_width && (
                    <p
                      id="min_width-error"
                      className="text-red-500 text-xs mt-1"
                      role="alert"
                    >
                      {errors.min_width}
                    </p>
                  )}
                </div>

                <div className="col-span-1">
                  <label
                    htmlFor="max_width-input"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Maximum Width (m)
                  </label>
                  <NumberInput
                    id="max_width-input"
                    name="max_width"
                    value={localFormData.max_width ?? ''}
                    onChange={(e) => handleNumberInputChange(e, 'max_width')}
                    disabled={mode === 'view' || isSubmitting}
                    className={clsx(
                      'input input-bordered w-full h-11 rounded-lg border-gray-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
                      errors.max_width && 'border-red-500'
                    )}
                    aria-invalid={!!errors.max_width}
                    aria-describedby={
                      errors.max_width ? 'max_width-error' : undefined
                    }
                    placeholder="Enter maximum width (m)"
                    step="0.01"
                    min="1"
                    max="100"
                    autoComplete="off"
                  />
                  {errors.max_width && (
                    <p
                      id="max_width-error"
                      className="text-red-500 text-xs mt-1"
                      role="alert"
                    >
                      {errors.max_width}
                    </p>
                  )}
                </div>

                <div className="col-span-1">
                  <label
                    htmlFor="min_depth-input"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Minimum Depth (m)
                  </label>
                  <NumberInput
                    id="min_depth-input"
                    name="min_depth"
                    value={localFormData.min_depth ?? ''}
                    onChange={(e) => handleNumberInputChange(e, 'min_depth')}
                    disabled={mode === 'view' || isSubmitting}
                    className={clsx(
                      'input input-bordered w-full h-11 rounded-lg border-gray-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
                      errors.min_depth && 'border-red-500'
                    )}
                    aria-invalid={!!errors.min_depth}
                    aria-describedby={
                      errors.min_depth ? 'min_depth-error' : undefined
                    }
                    placeholder="Enter minimum depth (m)"
                    step="0.01"
                    min="0"
                    max="100"
                    autoComplete="off"
                  />
                  {errors.min_depth && (
                    <p
                      id="min_depth-error"
                      className="text-red-500 text-xs mt-1"
                      role="alert"
                    >
                      {errors.min_depth}
                    </p>
                  )}
                </div>

                <div className="col-span-1 pb-1">
                  <label
                    htmlFor="max_depth-input"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Maximum Depth (m)
                  </label>
                  <NumberInput
                    id="max_depth-input"
                    name="max_depth"
                    value={localFormData.max_depth ?? ''}
                    onChange={(e) => handleNumberInputChange(e, 'max_depth')}
                    disabled={mode === 'view' || isSubmitting}
                    className={clsx(
                      'input input-bordered w-full h-11 rounded-lg border-gray-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
                      errors.max_depth && 'border-red-500'
                    )}
                    aria-invalid={!!errors.max_depth}
                    aria-describedby={
                      errors.max_depth ? 'max_depth-error' : undefined
                    }
                    placeholder="Enter maximum depth (m)"
                    step="0.01"
                    min="0"
                    max="100"
                    autoComplete="off"
                  />
                  {errors.max_depth && (
                    <p
                      id="max_depth-error"
                      className="text-red-500 text-xs mt-1"
                      role="alert"
                    >
                      {errors.max_depth}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {(mode === 'create' || mode === 'edit') && (
          <div className="mt-8 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => onClose?.()}
              className="btn btn-sm btn-ghost text-gray-600 hover:bg-gray-100 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-gray-500"
              aria-label="Cancel"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={onSubmit}
              className="btn btn-sm btn-primary text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label={
                mode === 'create' ? 'Create Berth Category' : 'Save Changes'
              }
              disabled={isSubmitting}
            >
              {isSubmitting
                ? 'Saving...'
                : mode === 'create'
                  ? 'Create'
                  : 'Save'}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export { BerthCategoryModal };
